import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  Building2,
  Loader2,
  CheckCircle2,
  Download,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useSearchShortcut } from "@/lib/use-search-shortcut";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { AddContactWithAIDialog } from "@/components/add-contact-with-ai-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

type EnrichmentStatus = "pending" | "running" | "succeeded" | "failed";
type IndustryType = "accounting" | "business";

type ClientRow = {
  id: string;
  companyName: string;
  regCode: string | null;
  city: string | null;
  industry: string | null;
  industryType: IndustryType | null;
  accountingSystem: string | null;
  headcount: number | null;
  revenueEur: number | null;
  isEnriched: boolean;
  enrichmentStatus: EnrichmentStatus;
  fiberyExportedAt: Date | string | null;
  updatedAt: Date | string;
};

const INDUSTRY_TYPE_BADGE: Record<IndustryType, { label: string; tone: string }> = {
  accounting: { label: "Accounting", tone: "bg-blue-100 text-blue-700 border-blue-300" },
  business: { label: "Business", tone: "bg-amber-100 text-amber-800 border-amber-300" },
};

const STATUS_LABELS: Record<
  EnrichmentStatus,
  { idleLabel: string; busyLabel: string; icon: typeof Download; tone: string }
> = {
  pending: {
    idleLabel: "Get data",
    busyLabel: "Queued…",
    icon: Download,
    tone: "bg-zinc-100 text-zinc-700 hover:bg-zinc-200",
  },
  running: {
    idleLabel: "Preparing…",
    busyLabel: "Preparing…",
    icon: Loader2,
    tone: "bg-blue-100 text-blue-700",
  },
  succeeded: {
    idleLabel: "Ready",
    busyLabel: "Refreshing…",
    icon: CheckCircle2,
    tone: "bg-emerald-100 text-emerald-700 hover:bg-emerald-200",
  },
  failed: {
    idleLabel: "Get data",
    busyLabel: "Retrying…",
    icon: Download,
    tone: "bg-zinc-100 text-zinc-700 hover:bg-zinc-200",
  },
};

export function ClientsPage() {
  const [search, setSearch] = useState("");
  const [aiContactOpen, setAiContactOpen] = useState(false);
  const searchInputRef = useSearchShortcut();
  const list = trpc.clients.list.useQuery(
    { search: search || undefined },
    {
      refetchInterval: 1000,
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }
  );

  const rows = (list.data as ClientRow[] | undefined) ?? [];

  return (
    <div className="mx-auto w-full max-w-screen-2xl px-4 py-6 md:px-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Clients</h1>
          <p className="text-sm text-muted-foreground">
            {rows.length} clients · {rows.filter((c) => c.isEnriched).length} ready
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAiContactOpen(true)}
            title="Paste an email signature and let AI extract a contact"
          >
            <Sparkles className="mr-1.5 h-3.5 w-3.5" />
            Add contact with AI
          </Button>
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              placeholder="Search by name, code, city..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>
      </div>

      <AddContactWithAIDialog
        open={aiContactOpen}
        onOpenChange={setAiContactOpen}
      />

      {list.isLoading ? (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed p-12 text-center">
          <Building2 className="mx-auto h-10 w-10 text-muted-foreground/30" />
          <p className="mt-4 text-sm text-muted-foreground">
            No clients yet. Create an opportunity — the client will be added automatically.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table className="min-w-[880px]">
            <TableHeader>
              <TableRow>
                <TableHead className="h-10">Company</TableHead>
                <TableHead className="h-10">Type</TableHead>
                <TableHead className="h-10">Fibery</TableHead>
                <TableHead className="h-10">Reg. code</TableHead>
                <TableHead className="h-10">City</TableHead>
                <TableHead className="h-10 text-right">Headcount</TableHead>
                <TableHead className="h-10 text-right">Revenue</TableHead>
                <TableHead className="h-10">Accounting</TableHead>
                <TableHead className="h-10 text-right">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((c) => (
                <ClientListRow key={c.id} client={c} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function ClientListRow({ client }: { client: ClientRow }) {
  const utils = trpc.useUtils();
  const status = STATUS_LABELS[client.enrichmentStatus];
  const successIcon =
    client.enrichmentStatus === "succeeded" ? RefreshCw : status.icon;
  const Icon = client.enrichmentStatus === "running" ? Loader2 : successIcon;

  const request = trpc.clients.requestEnrichment.useMutation({
    onSuccess: () => {
      toast.success(`Fetching data for ${client.companyName}…`);
      utils.clients.list.invalidate();
      utils.clients.get.invalidate({ id: client.id });
    },
    onError: (err) => toast.error(err.message),
  });

  const isBusy = request.isPending || client.enrichmentStatus === "running";
  const label = isBusy ? status.busyLabel : status.idleLabel;
  const isClickable = !isBusy;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isBusy) return;
    request.mutate({ id: client.id });
  };

  return (
    <TableRow>
      <TableCell className="py-2">
        <Link
          to={`/clients/${client.id}`}
          className="block font-medium hover:underline"
        >
          {client.companyName}
        </Link>
        {client.industry && (
          <div className="line-clamp-1 text-xs text-muted-foreground">
            {client.industry}
          </div>
        )}
      </TableCell>
      <TableCell className="py-2">
        {client.industryType ? (
          <span
            className={cn(
              "inline-block rounded-full border px-2 py-0.5 text-xs font-medium",
              INDUSTRY_TYPE_BADGE[client.industryType].tone
            )}
          >
            {INDUSTRY_TYPE_BADGE[client.industryType].label}
          </span>
        ) : (
          <span className="text-muted-foreground/50">—</span>
        )}
      </TableCell>
      <TableCell className="py-2">
        {client.fiberyExportedAt ? (
          <span className="inline-block rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700">
            Fibery ✓
          </span>
        ) : (
          <span className="text-muted-foreground/50">—</span>
        )}
      </TableCell>
      <TableCell className="py-2 text-sm">
        {client.regCode || <span className="text-muted-foreground/50">—</span>}
      </TableCell>
      <TableCell className="py-2 text-sm">
        {client.city || <span className="text-muted-foreground/50">—</span>}
      </TableCell>
      <TableCell className="py-2 text-right text-sm">
        {client.headcount ?? <span className="text-muted-foreground/50">—</span>}
      </TableCell>
      <TableCell className="py-2 text-right text-sm">
        {client.revenueEur ? (
          `€${formatNumber(client.revenueEur)}`
        ) : (
          <span className="text-muted-foreground/50">—</span>
        )}
      </TableCell>
      <TableCell className="py-2 text-sm">
        {client.accountingSystem || (
          <span className="text-muted-foreground/50">—</span>
        )}
      </TableCell>
      <TableCell className="py-2 text-right">
        <button
          type="button"
          onClick={handleClick}
          disabled={isBusy}
          title={isClickable ? "Fetch data for this client" : "Fetch in progress"}
          className={cn(
            "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium transition",
            status.tone,
            isClickable ? "cursor-pointer" : "cursor-default opacity-90"
          )}
        >
          <Icon className={cn("mr-1 h-3 w-3", isBusy && "animate-spin")} />
          {label}
        </button>
      </TableCell>
    </TableRow>
  );
}

function formatNumber(n: number): string {
  return n.toLocaleString("en-US");
}
